import { computed, onUnmounted, ref, shallowRef, watch, type ComputedRef, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import useMyFetch from "@stores/useMyFetch";
import type { AnalysisCardState } from "@/components/Survey/ResultsV2/Cards/AnalysisCardShell.vue";

// POSTs `body` to <resultsApiPath>/<endpoint> (the survey or the shared dashboard), debounced, stale responses dropped; a null body means unconfigured.
export function useAnalysisQuery<T>(endpoint: string, body: ComputedRef<Record<string, unknown> | null>) {
  const { resultsApiPath } = storeToRefs(useSurveyStore());

  const fetched = ref(null) as Ref<T | null>;
  // The body the fetched result answers, and its serialized form: the content moves on as soon
  // as the user edits the panel, up to 300 ms plus a round trip before the next response lands,
  // and until then the result is stale and the card shows the loading state rather than old
  // numbers under new labels. Compared by payload, so a content edit that leaves the payload
  // as it is (a stage label, a display option) keeps the result and skips the round trip.
  const requested = shallowRef<Record<string, unknown> | null>(null);
  const requestedKey = ref<string | null>(null);
  const fetching = ref(false);
  const stale = computed(() => body.value !== null && JSON.stringify(body.value) !== requestedKey.value);
  const result = computed(() => (stale.value ? null : fetched.value));
  const loading = computed(() => fetching.value || stale.value);
  let lastRequestId = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  async function fetchData() {
    if (!body.value) {
      fetched.value = null;
      requested.value = null;
      requestedKey.value = null;
      return;
    }
    const sent = body.value;
    const key = JSON.stringify(sent);
    if (key === requestedKey.value && fetched.value !== null) {
      // Back on an answered payload: drop whatever is still in flight, its answer is for another body.
      lastRequestId += 1;
      fetching.value = false;
      return;
    }
    fetching.value = true;
    const requestId = ++lastRequestId;
    try {
      const { response, data } = await useMyFetch(`${resultsApiPath.value}/${endpoint}`)
        .post(body.value)
        .json<T>();
      if (requestId !== lastRequestId) return;
      fetched.value = response.value?.ok ? data.value : null;
      requested.value = sent;
      requestedKey.value = key;
    } finally {
      if (requestId === lastRequestId) fetching.value = false;
    }
  }

  function scheduleFetch() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchData, 300);
  }

  watch(body, scheduleFetch, { deep: true, immediate: true });
  onUnmounted(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
  });

  function state(hasData: boolean): AnalysisCardState {
    if (!body.value) return "unconfigured";
    if (result.value) return hasData ? "ready" : "empty";
    return loading.value ? "loading" : "empty";
  }

  return { result, loading, state, requested };
}
